import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Input } from '@attentive-platform/stem-ui';

import { apiErrorMessage } from '../../../Utils/apiErrors';
import { normalizeCompanies, searchBidOpsCompanies } from './api';
import { ChipList, ChipRemove, CompanyChip, CompanyMenu } from './styles';

const companyInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px'
  }
};

const CompanyOption = ({ company, onSelect }) => {
  const handleSelect = () => onSelect(company);

  return (
    <button
      type='button'
      className='block w-full cursor-pointer border-0 bg-transparent px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50'
      onClick={handleSelect}>
      {company.name}
    </button>
  );
};

const SelectedCompany = ({ company, onRemove }) => {
  const handleRemove = () => onRemove(company.id);

  return (
    <CompanyChip>
      {company.name}
      <ChipRemove type='button' aria-label={`Remove ${company.name}`} onClick={handleRemove}>
        ×
      </ChipRemove>
    </CompanyChip>
  );
};

const CompanyPicker = ({ selected, onChange, onError, multiple = true, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const rootRef = useRef(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const selectedCompany = multiple ? null : selected[0] || null;

  useEffect(() => {
    const handlePointerDown = event => {
      const target = event.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    const showingSelection = !multiple && selectedCompany?.name === trimmed;
    if (!open || trimmed.length < 1 || showingSelection) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      const { data, error } = await searchBidOpsCompanies({ q: trimmed, signal: controller.signal });
      if (controller.signal.aborted) return;
      setLoading(false);
      if (error) {
        setResults([]);
        onError(apiErrorMessage(error, 'Could not search companies'));
        return;
      }
      const selectedIds = new Set(selected.map(company => company.id));
      setResults(normalizeCompanies(data).filter(company => !selectedIds.has(company.id)));
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, selected, onError, open, multiple, selectedCompany]);

  const addCompany = company => {
    onChange(multiple ? [...selected, company] : [company]);
    setQuery(multiple ? '' : company.name);
    setResults([]);
    setOpen(false);
  };

  const removeCompany = id => {
    onChange(selected.filter(company => company.id !== id));
  };

  const handleInput = event => {
    if (disabled) return;
    const next = event.target.value;
    setQuery(next);
    setOpen(true);
    if (!multiple && selectedCompany && next !== selectedCompany.name) onChange([]);
  };

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
    if (!multiple && selectedCompany && !query) setQuery(selectedCompany.name);
  };

  const inputValue = !multiple && selectedCompany && !open ? selectedCompany.name : query;
  const showDropdown = !disabled && open && query.trim().length > 0 && query.trim() !== selectedCompany?.name;

  useEffect(() => {
    if (!showDropdown) return undefined;

    const placeMenu = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const gap = 4;
      const available = window.innerHeight - rect.bottom - gap - 12;
      setMenuBox({
        top: rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(240, Math.max(120, available))
      });
    };

    placeMenu();
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [showDropdown]);

  return (
    <div ref={rootRef}>
      <div ref={anchorRef}>
        <Input
          value={inputValue}
          className='w-full'
          placeholder='Search company name'
          disabled={disabled}
          onFocus={handleFocus}
          onChange={handleInput}
          sx={companyInputSx}
        />
        {showDropdown &&
          menuBox &&
          createPortal(
            <CompanyMenu
              ref={menuRef}
              top={menuBox.top}
              left={menuBox.left}
              width={menuBox.width}
              maxHeight={menuBox.maxHeight}>
              {loading && <div className='px-3 py-2 text-sm text-gray-500'>Searching…</div>}
              {!loading && results.length === 0 && (
                <div className='px-3 py-2 text-sm text-gray-500'>No companies found</div>
              )}
              {!loading &&
                results.map(company => <CompanyOption key={company.id} company={company} onSelect={addCompany} />)}
            </CompanyMenu>,
            document.body
          )}
      </div>
      {multiple && selected.length > 0 && (
        <ChipList>
          {selected.map(company => (
            <SelectedCompany key={company.id} company={company} onRemove={removeCompany} />
          ))}
        </ChipList>
      )}
    </div>
  );
};

export default CompanyPicker;
